import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { PartnerGroup } from '@/types/models'
import { Modal } from '@/components/Modal'
import { SearchBox } from '@/components/SearchBox'
import { Field, inputCls } from '@/components/Field'
import { useCrudMutations } from '@/lib/useCrudMutations'
import { useListNavigation } from '@/lib/useListNavigation'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'

const GROUP_FORM_FIELDS = ['name', 'biz_no', 'rep_name', 'memo'] as const

interface GroupForm {
  id?: number
  name: string
  biz_no: string
  rep_name: string
  memo: string
  is_active: boolean
  ignore_po: boolean
}

const blank: GroupForm = {
  name: '', biz_no: '', rep_name: '', memo: '', is_active: true, ignore_po: false,
}

export function PartnerGroupsTab() {
  const [search, setSearch] = useState('')
  const [edit, setEdit] = useState<GroupForm | null>(null)

  const { data: groups = [] } = useQuery({
    queryKey: ['partner-groups', search],
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) params.set('q', search)
      return api<PartnerGroup[]>(`/api/partner-groups?${params}`)
    },
  })

  const { save, remove } = useCrudMutations<GroupForm, PartnerGroup>({
    apiBase: '/api/partner-groups',
    queryKey: 'partner-groups',
    invalidateAlso: ['partners'],
    // 라우터가 trailing_slash=False → 뒤 슬래시 없이 호출 (슬래시 붙이면 404).
    onSaved: () => setEdit(null),
    deleteToast: '삭제됨',
  })

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(GROUP_FORM_FIELDS)

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<PartnerGroup>({
    items: groups,
    disabled: !!edit,
    onEnter: (g) => setEdit({
      id: g.id, name: g.name, biz_no: g.biz_no, rep_name: g.rep_name, memo: g.memo, is_active: g.is_active,
      ignore_po: g.ignore_po,
    }),
    onDelete: (g) => {
      void confirm(`상위 "${g.name}" 삭제? 멤버 파트너의 상위 배정만 해제됨.`, { danger: true }).then((ok) => ok && remove.mutate(g.id))
    },
  })

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* 등록된 그룹 */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-lg font-bold text-slate-800">등록된 상위 거래처</h3>
          <span className="text-xs text-slate-500">{groups.length}건</span>
          <div className="flex-1"/>
          <button onClick={() => setEdit({ ...blank })}
                  data-role="add-btn"
                  title="Alt+N"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5"/> 수동 등록
          </button>
        </div>
        <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200/60 mb-3">
          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
            <SearchBox value={search} onChange={setSearch} placeholder="상위명, 사업자번호 검색"/>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
              <tr>
                <th className="px-3 py-2.5 text-left font-medium">상위 거래처명</th>
                <th className="px-3 py-2.5 text-left font-medium w-32">사업자번호</th>
                <th className="px-3 py-2.5 text-left font-medium w-24">대표자</th>
                <th className="px-3 py-2.5 text-center font-medium w-20">멤버</th>
                <th className="px-3 py-2.5 text-right font-medium w-24"></th>
              </tr>
            </thead>
            <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
              {groups.map((g, idx) => (
                <tr key={g.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1}
                    className="border-t border-slate-100 hover:bg-slate-50 focus:bg-blue-50 focus:outline-none">
                  <td className="px-3 py-2 font-medium text-slate-900">{g.name}</td>
                  <td className="px-3 py-2 text-xs tabular-nums text-slate-600">{g.biz_no || '—'}</td>
                  <td className="px-3 py-2 text-slate-700">{g.rep_name || '—'}</td>
                  <td className="px-3 py-2 text-center tabular-nums text-slate-500">{g.member_count}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEdit({
                      id: g.id, name: g.name, biz_no: g.biz_no, rep_name: g.rep_name,
                      memo: g.memo, is_active: g.is_active, ignore_po: g.ignore_po,
                    })}
                            className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                    <button onClick={() => { void confirm(`상위 "${g.name}" 삭제? 멤버 파트너의 상위 배정만 해제됨.`, { danger: true }).then((ok) => ok && remove.mutate(g.id)) }}
                            className="text-rose-500 hover:text-rose-700 text-xs inline-flex items-center gap-0.5">
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </td>
                </tr>
              ))}
              {groups.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8 text-sm">
                  등록된 상위 거래처가 없습니다. "수동 등록"으로 시작하세요.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? '상위 거래처 수정' : '상위 거래처 등록'}>
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => { e.preventDefault(); save.mutate(edit) }}
                className="grid grid-cols-2 gap-3">
            <Field label="상위 거래처명 *" full>
              <input data-form-field="name" autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                     required className={inputCls}/>
            </Field>
            <Field label="사업자번호">
              <input data-form-field="biz_no" value={edit.biz_no} onChange={(e) => setEdit({ ...edit, biz_no: e.target.value })}
                     className={inputCls + ' tabular-nums'}/>
            </Field>
            <Field label="대표자">
              <input data-form-field="rep_name" value={edit.rep_name} onChange={(e) => setEdit({ ...edit, rep_name: e.target.value })}
                     className={inputCls}/>
            </Field>
            <Field label="비고" full>
              <textarea data-form-field="memo" value={edit.memo} onChange={(e) => setEdit({ ...edit, memo: e.target.value })}
                        rows={2} className={inputCls}/>
            </Field>
            <label className="col-span-2 flex items-start gap-2 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <input type="checkbox" checked={edit.ignore_po}
                     onChange={(e) => setEdit({ ...edit, ignore_po: e.target.checked })}
                     className="accent-emerald-600 w-4 h-4 mt-0.5"/>
              <span>
                발주번호 무시 (업장별 1전표로 병합)
                <span className="block text-xs text-slate-400 mt-0.5">
                  켜면 발주서 변환 시 PO번호를 무시하고 상단 업장별로 한 전표로 묶습니다.
                  소피텔·엠갤러리처럼 같은 업장이 여러 PO로 쪼개질 때 사용.
                </span>
              </span>
            </label>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)}
                      className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit"
                      className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg">
                저장
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
